import type { Ws } from '@/app/workspace'
import { diffDays } from '@/domain/dates'
import { planningProgress } from '@/domain/vacations'
import { formatDate } from '@/lib/format'

export interface Alert {
  id: string
  tone: 'danger' | 'warning' | 'accent' | 'success' | 'neutral'
  kind: 'loss' | 'goal' | 'decision' | 'approval' | 'info'
  title: string
  body?: string
  to: string
  cta?: string
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * Alerts derived from the data (no backend needed). Wording follows two UX rules:
 * - loss aversion: unused days are framed as days you will LOSE, with the date;
 * - goal gradient: the legal period is framed as how little is MISSING.
 */
export function buildAlerts(ws: Ws): Alert[] {
  const out: Alert[] = []
  const { me, today, year } = ws
  const settings = ws.data.organization.settings

  if (ws.can('requests.approve')) {
    const n = ws.toDecide.requests.length + ws.toDecide.changes.length
    if (n) out.push({ id: 'approvals', tone: 'accent', kind: 'approval', title: `${plural(n, 'pedido aguarda', 'pedidos aguardam')} a sua decisão`, body: 'A equipa planeia melhor quando a resposta chega cedo.', to: '/approvals', cta: 'Decidir' })
  }
  if (!me) return out

  const b = ws.balanceOf(me.id)
  if (b.carryOverLeft > 0) {
    const left = diffDays(today, b.carryOverDeadline)
    out.push({
      id: 'carry',
      tone: left <= settings.carryOverReminderDays ? 'danger' : 'warning',
      kind: 'loss',
      title: `Vai perder ${plural(b.carryOverLeft, 'dia transitado', 'dias transitados')} a ${formatDate(b.carryOverDeadline)}`,
      body: left === 0 ? 'Hoje é o último dia para os usar.' : `Faltam ${plural(left, 'dia', 'dias')}. Marque-os agora para não os perder.`,
      to: '/new',
      cta: 'Usar agora',
    })
  }

  const legal = ws.legalOf(me.id)
  if (!legal.met) {
    const enough = b.available >= legal.missing
    out.push({
      id: 'legal',
      tone: enough ? 'accent' : 'danger',
      kind: 'goal',
      title: legal.longest > 0 ? `Faltam ${plural(legal.missing, 'dia', 'dias')} para o período obrigatório de ${legal.required}` : `Ainda não tem o período de ${legal.required} dias úteis seguidos`,
      body: enough ? 'A lei exige um período contínuo por ano. Já sugerimos as melhores datas.' : 'O saldo que resta não chega. Fale com o seu gestor.',
      to: '/new?plan=long',
      cta: 'Ver sugestões',
    })
  }

  const month = Number(today.slice(5, 7))
  if (month >= 9 && b.available > 0) {
    const nextDeadline = `${year + 1}-${settings.carryOverDeadline}`
    out.push({
      id: 'year-end',
      tone: 'warning',
      kind: 'loss',
      title: `${plural(b.available, 'dia', 'dias')} de ${year} ainda por marcar`,
      body: settings.carryOverEnabled
        ? `Os que não gozar passam para ${year + 1}, mas perdem-se a ${formatDate(nextDeadline)}.`
        : 'Os dias que não gozar até 31 de dezembro perdem-se.',
      to: '/new',
      cta: 'Planear',
    })
  } else {
    const p = planningProgress(b)
    if (p.planned > 0 && p.planned < p.total) {
      out.push({ id: 'progress', tone: 'neutral', kind: 'goal', title: `${p.planned} de ${p.total} dias já planeados`, body: 'Está quase. Planear cedo garante as datas que quer.', to: '/new' })
    }
  }

  for (const r of ws.data.requests) {
    if (r.employeeId !== me.id || !r.decidedAt || (r.status !== 'approved' && r.status !== 'rejected')) continue
    if (diffDays(r.decidedAt.slice(0, 10), today) > 14) continue
    out.push({
      id: `decision-${r.id}`,
      tone: r.status === 'approved' ? 'success' : 'danger',
      kind: 'decision',
      title: r.status === 'approved' ? `Férias aprovadas: ${formatDate(r.start)} a ${formatDate(r.end)}` : `Pedido recusado: ${formatDate(r.start)} a ${formatDate(r.end)}`,
      body: r.decisionComment ?? undefined,
      to: '/requests',
    })
  }
  return out
}
